import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  CatalogEntityHit,
  ComplianceItem,
  ComponentVersion,
  Technology,
  VersionPolicy,
  TechnologyCategory,
} from './types';

export class StackPoliciesClient {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('stack-policies');
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const base = await this.baseUrl();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };
    const response = await this.fetchApi.fetch(`${base}${path}`, {
      ...options,
      headers,
    });
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        const body = await response.json();
        message = body.error || body.message || message;
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    if (response.status === 204) return undefined as T;
    return response.json();
  }

  listTechnologies(): Promise<Technology[]> {
    return this.request('/technologies');
  }

  createTechnology(body: {
    name: string;
    key?: string;
    description?: string;
    category?: TechnologyCategory;
  }): Promise<Technology> {
    return this.request('/technologies', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  deleteTechnology(id: string): Promise<void> {
    return this.request(`/technologies/${id}`, { method: 'DELETE' });
  }

  listPolicies(technologyId?: string): Promise<VersionPolicy[]> {
    const qs = technologyId
      ? `?technologyId=${encodeURIComponent(technologyId)}`
      : '';
    return this.request(`/policies${qs}`);
  }

  createPolicy(body: Record<string, unknown>): Promise<VersionPolicy> {
    return this.request('/policies', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updatePolicy(
    id: string,
    body: Record<string, unknown>,
  ): Promise<VersionPolicy> {
    return this.request(`/policies/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  deletePolicy(id: string): Promise<void> {
    return this.request(`/policies/${id}`, { method: 'DELETE' });
  }

  listAssignments(entityRef?: string): Promise<ComponentVersion[]> {
    const qs = entityRef ? `?entityRef=${encodeURIComponent(entityRef)}` : '';
    return this.request(`/assignments${qs}`);
  }

  createAssignment(body: Record<string, unknown>): Promise<ComponentVersion> {
    return this.request('/assignments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  updateAssignment(
    id: string,
    body: Record<string, unknown>,
  ): Promise<ComponentVersion> {
    return this.request(`/assignments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  deleteAssignment(id: string): Promise<void> {
    return this.request(`/assignments/${id}`, { method: 'DELETE' });
  }

  listCompliance(status?: string): Promise<ComplianceItem[]> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request(`/compliance${qs}`);
  }

  getEntityStatus(entityRef: string): Promise<ComplianceItem[]> {
    return this.request(`/entities/${encodeURIComponent(entityRef)}/status`);
  }

  searchCatalog(q?: string): Promise<CatalogEntityHit[]> {
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    return this.request(`/catalog-entities${qs}`);
  }

  scanRepos(
    body: {
      overwriteManual?: boolean;
      entityRef?: string;
    } = {},
  ): Promise<{
    scannedEntities: number;
    withRepo: number;
    upserted: number;
    skipped: number;
    errors: number;
    results: Array<{
      entityRef: string;
      repo?: string;
      detected: Array<{
        technologyKey: string;
        version: string;
        evidence: string;
      }>;
      upserted: Array<{
        technologyKey: string;
        version: string;
        action: string;
      }>;
      skipped: Array<{ technologyKey: string; reason: string }>;
      errors: string[];
    }>;
  }> {
    return this.request('/scan', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
}
